import { isEmpty, noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { QuickFilterToolbarItem } from 'src/components/FilterToolbar/QuickFilterToolbarItem';
import { Content } from 'src/contexts/ContentContext';
import { useMobileDrawerContext } from 'src/contexts/MobileDrawerContext';
import { vars } from 'src/core/themes';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { BadgedIcon } from 'src/core/ui/BadgedIcon/BadgedIcon';
import { useComputedState } from 'src/hooks/useComputedState';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './InventoryListingSortingContent.css';

export const InventoryCustomFilter = ({
  quickFilters,
  onResetCustomFilter,
}: {
  quickFilters: QuickFilterToolbarItem[] | undefined;
  onResetCustomFilter: () => void;
}) => {
  const { openWithContent, close } = useMobileDrawerContext();

  const selectedQuickFilter = useMemo(
    () => quickFilters?.find((filter) => filter.isSelected),
    [quickFilters]
  );

  const [localSelectedQuickFilter, setLocalSelectedFilter] = useComputedState<
    QuickFilterToolbarItem | undefined
  >(selectedQuickFilter);

  // Pneding quick filters from the custom filter bottomsheet
  const hasPendingQuickFilter = useMemo(() => {
    return (
      localSelectedQuickFilter &&
      localSelectedQuickFilter.id !== selectedQuickFilter?.id
    );
  }, [localSelectedQuickFilter, selectedQuickFilter?.id]);

  const onBackFromCustomFilters = useCallback(
    (filter?: QuickFilterToolbarItem) => {
      setLocalSelectedFilter(filter);
      close();
    },
    [close, setLocalSelectedFilter]
  );

  if (!quickFilters || isEmpty(quickFilters)) {
    return null;
  }

  return (
    <BadgedIcon
      icon={
        <Button
          variant="text"
          onClick={() =>
            openWithContent(
              <InventoryCustomFilterContent
                quickFilters={quickFilters}
                selectedQuickFilter={localSelectedQuickFilter}
                onClose={onBackFromCustomFilters}
                onResetCustomFilter={onResetCustomFilter}
              />,
              { size: '60%' }
            )
          }
        >
          <Content id={ContentId.CustomFilter} />
        </Button>
      }
      withBadge={hasPendingQuickFilter}
    />
  );
};

const InventoryCustomFilterContent = ({
  quickFilters,
  selectedQuickFilter,
  onClose,
  onResetCustomFilter,
}: {
  quickFilters: QuickFilterToolbarItem[];
  selectedQuickFilter?: QuickFilterToolbarItem;
  onClose: (_?: QuickFilterToolbarItem) => void;
  onResetCustomFilter: () => void;
}) => {
  const [localSelectedQuickFilter, setLocalSelectedFilter] = useComputedState<
    QuickFilterToolbarItem | undefined
  >(selectedQuickFilter);

  const onApplyCustomFilter = useCallback(() => {
    localSelectedQuickFilter?.onClick();
    onClose();
  }, [localSelectedQuickFilter, onClose]);

  // Disable apply button when
  // 1. no local quick filter
  // 2. local quick filter is the same as the existing quick filter
  const disabled = useMemo(() => {
    const currentQuickFilter = quickFilters.find((filter) => filter.isSelected);
    return (
      !localSelectedQuickFilter ||
      localSelectedQuickFilter.id === currentQuickFilter?.id
    );
  }, [localSelectedQuickFilter, quickFilters]);

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      gap="m"
      style={{ color: vars.color.textPrimary }}
    >
      <Stack width="full" alignItems="center" justifyContent="spaceBetween">
        <span className={styles.sortAndFilterHeader}>
          <Content id={ContentId.CustomFilter} />
        </span>
        <Button
          variant="text"
          disabled={quickFilters.every((filter) => !filter.isSelected)}
          onClick={onResetCustomFilter}
        >
          <Content id={ContentId.Clear} />
        </Button>
      </Stack>
      <Stack
        direction="column"
        width="full"
        style={{ flex: 1, overflowY: 'auto' }}
      >
        <RadioGroup value={localSelectedQuickFilter?.id} onValueChange={noop}>
          <Stack direction="column" width="full" gap="s">
            {quickFilters.map((quickFilter) => (
              <InventoryCutomFilterItem
                key={quickFilter.id}
                quickFilter={quickFilter}
                selected={localSelectedQuickFilter?.id === quickFilter.id}
                onSelect={() => setLocalSelectedFilter(quickFilter)}
              />
            ))}
          </Stack>
        </RadioGroup>
      </Stack>
      <Stack justifyContent="spaceBetween" width="full" alignItems="center">
        <Button
          style={{ height: '40px' }}
          shape="rect"
          variant="outlineGray"
          onClick={() => onClose(localSelectedQuickFilter)}
        >
          <Content id={ContentId.Back} />
        </Button>
        <Button
          style={{ height: '40px' }}
          shape="rect"
          disabled={disabled}
          onClick={onApplyCustomFilter}
        >
          <Content id={ContentId.Apply} />
        </Button>
      </Stack>
    </Stack>
  );
};

const InventoryCutomFilterItem = ({
  quickFilter,
  selected,
  onSelect,
}: {
  quickFilter: QuickFilterToolbarItem;
  selected: boolean;
  onSelect: () => void;
}) => {
  return (
    <Stack
      role="button"
      width="full"
      direction="column"
      className={styles.inventoryFilterGroupItem}
      style={{ cursor: 'pointer' }}
      gap="l"
      onClick={onSelect}
    >
      <Radio value={quickFilter.id} label={quickFilter.name} onChange={noop} />
    </Stack>
  );
};
